import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import * as pdfjsLib from "pdfjs-dist";
import { Looks } from "@mui/icons-material";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const ResumeExtractor = () => {
  const [parsedData, setParsedData] = useState(null);

  // Improved skill extraction: finds "Skills" section and splits by line, comma, or bullet
  const extractSkillsSection = (text) => {
    // Find the "Skills" section (case-insensitive)
    const match = text.match(
      /skills\s*[:\-]?\s*([\s\S]*?)(?:\n[A-Z][^\n]*\n|experience|education|projects|certifications|$)/i
    );
    if (!match) return [];
    // Get the section content
    const section = match[1];
    // Split by newlines, commas, bullets, or dots
    return section
      .split(/[\n,•·]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .filter((s) => s.length > 1 && !/^(skills?)$/i.test(s));
  };

  const parseResumeText = (text) => {
    const lines = text.split("\n").map((line) => line.trim());

    const email =
      text.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/)?.[0] || "";
    const phone =
      text.match(
        /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/
      )?.[0] || "";
    const name = lines[0] || "";
    const role = lines[1];
    const location = lines[2].split(" ")[0] || "";
    const summary = lines.find((line) => /summary|objective/i.test(line)) || "";

    const skills = extractSkillsSection(text);

    const urls = text.match(/https?:\/\/(www\.)?[a-zA-Z0-9._/-]+/g) || [];
    const socialLinks = {
      linkedin: urls.find((u) => u.includes("linkedin.com")) || "",
      github: urls.find((u) => u.includes("github.com")) || "",
      portfolio:
        urls.find(
          (u) => !u.includes("linkedin.com") && !u.includes("github.com")
        ) || "",
    };

    const extractSection = (keyword) => {
      const match =
        text
          .split(new RegExp(keyword, "i"))[1]
          ?.split(/education|skills|experience|projects|certifications/i)[0] ||
        "";
      return match;
    };

    const experienceBlocks = extractSection("experience");
    const experiences = experienceBlocks
      .split(/\n\n+/)
      .filter(Boolean)
      .slice(0, 3)
      .map((block) => ({
        company: block.match(/at\s+(.+)/i)?.[1] || "",
        position: block.split("\n")[0],
        duration: block.match(/\b\d{4}.*\d{4}|\b\d{4}.*present/i)?.[0] || "",
        description: block,
      }));

    const educationBlocks = extractSection("education");
    const education = educationBlocks
      .split(/\n\n+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((block) => ({
        institution: block.split("\n")[0],
        degree:
          block.match(/(B\.?Sc|M\.?Sc|Bachelor|Master|PhD)[^,\n]*/i)?.[0] || "",
        duration: block.match(/\b\d{4}.*\d{4}|\b\d{4}.*present/i)?.[0] || "",
      }));

    const projectBlocks = extractSection("projects");
    const projects = projectBlocks
      .split(/\n\n+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((block) => ({
        name: block.split("\n")[0],
        description: block,
      }));

    const certificationBlocks = extractSection("certifications|certification");
    const certifications = certificationBlocks
      .split(/\n+/)
      .filter(Boolean)
      .slice(0, 3)
      .map((cert) => ({
        name: cert,
      }));

    return {
      personalInfo: { name, role, Location, email, phone },
      summary,
      skills,
      experiences,
      education,
      projects,
      certifications,
      socialLinks,
    };
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    const reader = new FileReader();

    reader.onload = async function () {
      const typedArray = new Uint8Array(this.result);
      const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;

      let fullText = "";
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const strings = content.items.map((item) => item.str).join("\n");
        fullText += strings + "\n";
      }

      const data = parseResumeText(fullText);
      setParsedData(data);
    };

    reader.readAsArrayBuffer(file);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { "application/pdf": [] },
  });

  return (
    <div className="p-6 border-2 border-dashed rounded-lg max-w-3xl mx-auto">
      <div {...getRootProps()} className="cursor-pointer text-center py-4">
        <input {...getInputProps()} />
        <p className="text-gray-600">
          📄 Drag & drop a resume PDF here, or click to upload
        </p>
      </div>

      {parsedData && (
        <div className="mt-6 text-left space-y-4">
          <h2 className="text-xl font-bold">👤 Personal Info</h2>
          <p>
            <strong>Name:</strong> {parsedData.personalInfo.name}
          </p>
          <p>
            <strong>Name:</strong> {parsedData.personalInfo.role}
          </p>
          <p>
            <strong>Name:</strong> {parsedData.personalInfo.location}
          </p>
          <p>
            <strong>Email:</strong> {parsedData.personalInfo.email}
          </p>
          <p>
            <strong>Phone:</strong> {parsedData.personalInfo.phone}
          </p>

          <h2 className="text-xl font-bold mt-4">🔗 Social Links</h2>
          <ul>
            {parsedData.socialLinks.linkedin && (
              <li>
                <a
                  href={parsedData.socialLinks.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </li>
            )}
            {parsedData.socialLinks.github && (
              <li>
                <a
                  href={parsedData.socialLinks.github}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>
              </li>
            )}
            {parsedData.socialLinks.portfolio && (
              <li>
                <a
                  href={parsedData.socialLinks.portfolio}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Portfolio
                </a>
              </li>
            )}
          </ul>

          <h2 className="text-xl font-bold mt-4">📝 Summary</h2>
          <p>{parsedData.summary}</p>

          <h2 className="text-xl font-bold mt-4">🛠 Skills</h2>
          <ul className="list-disc list-inside">
            {parsedData.skills.map((skill, i) => (
              <li key={i}>{skill}</li>
            ))}
          </ul>

          <h2 className="text-xl font-bold mt-4">💼 Experience</h2>
          {parsedData.experiences.map((exp, i) => (
            <div key={i} className="mb-2">
              <p>
                <strong>{exp.position}</strong> at {exp.company}
              </p>
              <p className="text-sm text-gray-600">{exp.duration}</p>
              <p>{exp.description}</p>
            </div>
          ))}

          <h2 className="text-xl font-bold mt-4">🎓 Education</h2>
          {parsedData.education.map((edu, i) => (
            <div key={i} className="mb-2">
              <p>
                <strong>{edu.institution}</strong> - {edu.degree}
              </p>
              <p className="text-sm text-gray-600">{edu.duration}</p>
            </div>
          ))}

          <h2 className="text-xl font-bold mt-4">📁 Projects</h2>
          {parsedData.projects.map((proj, i) => (
            <div key={i} className="mb-2">
              <p>
                <strong>{proj.name}</strong>
              </p>
              <p>{proj.description}</p>
            </div>
          ))}

          <h2 className="text-xl font-bold mt-4">🏅 Certifications</h2>
          <ul className="list-disc list-inside">
            {parsedData.certifications.map((cert, i) => (
              <li key={i}>{cert.name}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ResumeExtractor;
